import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Button from '../../components/button/Button';
import './Questionnaire.scss';
import roundFill from '../../assets/icons/round-fill.svg';
import round from '../../assets/icons/round.svg';
import { useDispatch, useSelector } from 'react-redux';
import * as action from '../../store/actions/filled-questionnaire-action';
import api from '../../utils/apiUtils';
import Option from '../../components/options/Options';

interface IAnswer {
    id: string,
    text: string,
    isEligibleToProceed?: boolean,
    score?: number
}

interface IFilledAnswer {
    answersId: string,
    userInput: string
}

interface IQuestion {
    questionsId: string,
    filledAnswers: Array<IFilledAnswer>
}

interface ISection {
    sectionsId: string,
    numberOfEligibleAnswers: number,
    filledQuestions: Array<IQuestion>
}

type ParamsType = {
    mode: string;
};

const Questionnaire = () => {
    const {mode} = useParams<ParamsType>();
    let navigate = useNavigate();
    let dispatch = useDispatch();
    const location = useLocation();

    // Track current section and phase indices from Redux or navigation state
    const storedSectionIndex = useSelector((store: any) => store.filledQuestionnaire.currentSectionIndex);
    const storedPhaseIndex = useSelector((store: any) => store.filledQuestionnaire.currentPhaseIndex);
    
    // Check if coming from transition with explicit indices
    const navState = location.state as any;
    const initialSectionIndex = navState?.sectionIndex !== undefined ? navState.sectionIndex : (storedSectionIndex || 0);
    const initialPhaseIndex = navState?.phaseIndex !== undefined ? navState.phaseIndex : (storedPhaseIndex || 0);
    
    const [sectionIndex, setSectionIndex] = useState<number>(initialSectionIndex);
    const [phaseIndex, setPhaseIndex] = useState<number>(initialPhaseIndex);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);
    const [showToast, setShowToast] = useState(false);
    const sections = useSelector((store: any) => store.questionnaire.sections);
    let filledQuestionnaire = useSelector((store: any) => store.filledQuestionnaire);

    // Sync local state with Redux or navigation state
    useEffect(() => {
        if (navState?.sectionIndex !== undefined) {
            setSectionIndex(navState.sectionIndex);
            setPhaseIndex(navState.phaseIndex);
        } else {
            setSectionIndex(storedSectionIndex || 0);
            setPhaseIndex(storedPhaseIndex || 0);
        }
    }, [storedSectionIndex, storedPhaseIndex, navState?.sectionIndex, navState?.phaseIndex]);

    // Get current section and phase
    const currentSection = sections[sectionIndex];
    const currentPhase = currentSection?.phases?.[phaseIndex];

    const initialFormData = {
        sectionsId: "",
        phasesId: "",
        numberOfEligibleAnswers: 0,
        filledQuestions: []  as Array<IQuestion>
    };

    const [values, setValues] = useState<{[index: string]:any}>({});
    const [formData, setFormData] = useState<any>(initialFormData);
    const [answers, setAnswers] = useState<{[index: string]:any}>({});

    window.onload = function() {
        window.location.pathname = '/'
    };

    // Helper function to update position in both local state and Redux
    const updatePosition = (newSectionIndex: number, newPhaseIndex: number) => {
        setSectionIndex(newSectionIndex);
        setPhaseIndex(newPhaseIndex);
        dispatch({
            type: action.SET_CURRENT_POSITION,
            payload: {
                sectionIndex: newSectionIndex,
                phaseIndex: newPhaseIndex
            }
        });
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
            setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    useEffect(() => {
        // Reset phase index when section changes
        if (sectionIndex >= sections.length) {
            updatePosition(0, 0);
        }
    }, [sectionIndex, sections]);

    // Scroll to top when phase changes
    useEffect(() => {
        // Use setTimeout to ensure DOM has updated
        setTimeout(() => {
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0; // For Safari
        }, 100);
    }, [sectionIndex, phaseIndex]);

    // Restore saved answers when navigating to a phase
    useEffect(() => {
        if (currentSection && currentPhase) {
            // Find saved data for this section and phase from Redux store
            const savedSection = filledQuestionnaire.filledSections?.find(
                (section: any) => section.sectionsId === currentSection.id
            );

            if (savedSection) {
                const savedPhase = savedSection.filledPhases?.find(
                    (phase: any) => phase.phasesId === currentPhase.id
                );

                if (savedPhase && savedPhase.filledQuestions) {
                    // Restore values and formData from saved phase
                    const restoredValues: {[index: string]: any} = {};
                    const restoredAnswers: {[index: string]: any} = {};

                    savedPhase.filledQuestions.forEach((question: any) => {
                        if (question.filledAnswers && question.filledAnswers.length > 0) {
                            const answerId = question.filledAnswers[0].answersId;
                            restoredValues[question.questionsId] = answerId;

                            // Find the answer details to restore score
                            const questionData = currentPhase.questions?.find(
                                (q: any) => q.id === question.questionsId
                            );
                            if (questionData) {
                                const answerData = questionData.answers?.find(
                                    (a: any) => a.id === answerId
                                );
                                if (answerData) {
                                    restoredAnswers[question.questionsId] = {
                                        score: answerData.score || 0,
                                        sectionIndex: sectionIndex,
                                        phaseIndex: phaseIndex
                                    };
                                }
                            }
                        }
                    });

                    setValues(restoredValues);
                    setAnswers(restoredAnswers);
                    setFormData({
                        sectionsId: currentSection.id,
                        phasesId: currentPhase.id,
                        numberOfEligibleAnswers: 0,
                        filledQuestions: savedPhase.filledQuestions
                    });
                } else {
                    // No saved data for this phase, reset to empty
                    setValues({});
                    setAnswers({});
                    setFormData(initialFormData);
                }
            } else {
                // No saved data for this section, reset to empty
                setValues({});
                setAnswers({});
                setFormData(initialFormData);
            }
        }
    }, [currentSection?.id, currentPhase?.id, sectionIndex, phaseIndex]);

    // Get questions for the current phase
    const getCurrentPhaseQuestions = () => {
        if (!currentPhase || !currentPhase.questions) return [];
        return currentPhase.questions;
    };

    // Get phases for the current section only
    const getCurrentSectionPhases = () => {
        if (!currentSection || !currentSection.phases) return [];
        return currentSection.phases.map((phase: any, idx: number) => ({
            ...phase,
            phaseIndex: idx
        }));
    };

    const currentSectionPhases = getCurrentSectionPhases();

    const fieldChanged = (answer: IAnswer, questionId: string) => {
        setValues((currentValues: {[index: string]:any}) => {
            currentValues[questionId] = answer.id;
            return currentValues;
        });
        setFormData({
            ...formData,
            sectionsId: currentSection?.id,
            phasesId: currentPhase?.id,
            filledQuestions: [...formData.filledQuestions.filter((question: IQuestion) => question.questionsId !== questionId),
                 {questionsId: questionId, filledAnswers: [{answersId: answer.id, userInput: answer.text}]}]
        });

        setAnswers((currentAnswers: {[index: string]:any}) => {
            currentAnswers[questionId] = {
                score: answer.score || 0,
                sectionIndex: sectionIndex,
                phaseIndex: phaseIndex
            };
            return currentAnswers;
        });
    };

    // Calculate if phase score meets threshold (>50% of max score)
    const calculatePhaseScore = () => {
        const phaseQuestions = getCurrentPhaseQuestions();
        let totalScore = 0;
        let maxPossibleScore = 0;

        phaseQuestions.forEach((question: any) => {
            const answer = answers[question.id];
            if (answer) {
                totalScore += answer.score || 0;
            }
            // Assuming max score per question is 4 (highest answer score)
            maxPossibleScore += 4;
        });

        const maxScore = currentPhase?.maxScore || maxPossibleScore;
        const percentage = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;

        return {
            totalScore,
            maxScore,
            percentage,
            passesThreshold: percentage > 50
        };
    };

    const moveToNextPhase = () => {
        const scoreResult = calculatePhaseScore();

        // Save current phase data
        if (formData.sectionsId && formData.phasesId) {
            dispatch({
                type: action.ADD_SECTIONS,
                payload: {
                    formData: {
                        ...formData,
                        maxScore: scoreResult.maxScore
                    },
                    lastCompletedPhasesId: currentPhase?.id,
                    phaseScore: scoreResult.totalScore
                }
            });
        }

        if (scoreResult.passesThreshold && phaseIndex < (currentSection?.phases?.length - 1)) {
            // Move to next phase in same section
            const newPhaseIndex = phaseIndex + 1;
            
            // Update position in Redux
            dispatch({
                type: action.SET_CURRENT_POSITION,
                payload: {
                    sectionIndex: sectionIndex,
                    phaseIndex: newPhaseIndex
                }
            });
            
            setFormData(initialFormData);
            setValues({});
            
            // Navigate with state to ensure correct phase is shown
            navigate('/questionnaire', {
                replace: true,
                state: {
                    sectionIndex: sectionIndex,
                    phaseIndex: newPhaseIndex
                }
            });
        } else {
            // Move to next section, first phase
            if (sectionIndex < sections.length - 1) {
                const nextSectionData = sections[sectionIndex + 1];
                const newSectionIndex = sectionIndex + 1;
                const newPhaseIndex = 0;
                
                // Update position in Redux
                dispatch({
                    type: action.SET_CURRENT_POSITION,
                    payload: {
                        sectionIndex: newSectionIndex,
                        phaseIndex: newPhaseIndex
                    }
                });
                
                // Navigate to transition page with section info and position
                navigate('/section-transition', {
                    state: {
                        currentSection: currentSection?.name,
                        nextSection: nextSectionData?.name,
                        sectionIndex: newSectionIndex,
                        phaseIndex: newPhaseIndex
                    }
                });
                
                setFormData(initialFormData);
                setValues({});
            } else {
                // End of questionnaire - go to leave note page
                navigate('/leave-note');
            }
        }
    };

    const moveToPreviousPhase = () => {
        // Save current phase data before going back
        if (formData.sectionsId && formData.phasesId && formData.filledQuestions.length > 0) {
            const scoreResult = calculatePhaseScore();
            dispatch({
                type: action.ADD_SECTIONS,
                payload: {
                    formData: {
                        ...formData,
                        maxScore: scoreResult.maxScore
                    },
                    lastCompletedPhasesId: currentPhase?.id,
                    phaseScore: scoreResult.totalScore
                }
            });
        }

        if (phaseIndex > 0) {
            // Go to previous phase in same section
            updatePosition(sectionIndex, phaseIndex - 1);
        } else if (sectionIndex > 0) {
            // Go to last phase of previous section
            const prevSection = sections[sectionIndex - 1];
            updatePosition(sectionIndex - 1, (prevSection?.phases?.length || 1) - 1);
        } else {
            // At the beginning, go back to landing
            navigate('/landing-page/edit');
            return;
        }

        // Don't reset formData and values here - they will be restored by the useEffect
        navigate('/questionnaire/edit');
    };

    const previousClick = () => {
        moveToPreviousPhase();
    }

    const modifyFilledQuestionnaire = () => {
        //TODO: sta ako se vrati i izmeni odg i ne prodje dalje?
        filledQuestionnaire.filledSections = filledQuestionnaire.filledSections.filter((section: any) => section.sectionId !== formData.sectionsId);
        filledQuestionnaire.filledSections.push(formData)
    }

    const sendFilledQuestionnaire = () => {
        // Save last phase data before sending
        if (formData.sectionsId && formData.phasesId) {
            mode === 'edit' ? modifyFilledQuestionnaire(): filledQuestionnaire.filledSections.push(formData);
        }
        filledQuestionnaire.lastCompletedPhasesId = currentPhase?.id;
        api.post(`/filled-questionnaires`, filledQuestionnaire).then(response => {
                updatePosition(0, 0);
                navigate('/thank-you-page');
            }
        )
        .catch(err => {
            alert('Error: Invalid URL');
        });
    }

    const nextClick = (e: any) => {
        e.preventDefault();

        // Check if all mandatory questions are answered
        const phaseQuestions = getCurrentPhaseQuestions();
        const unansweredQuestions = phaseQuestions.filter((question: any) =>
            question.isMandatory && !values[question.id]
        );

        if (unansweredQuestions && unansweredQuestions.length > 0) {
            setShowToast(true);
            setTimeout(() => setShowToast(false), 5000);
            return;
        }

        // Move to next phase/section based on score
        moveToNextPhase();
    }

    return (
        <>
            <style>{`
                body, html {
                    margin: 0;
                    padding: 0;
                    background-color: #1C1D1F !important;
                    overflow-x: hidden;
                    scroll-behavior: smooth;
                    max-width: 100vw;
                }
                * {
                    box-sizing: border-box;
                    max-width: 100%;
                }

                #root {
                    overflow-x: hidden;
                }

                /* Custom radio button styles for the checked state */
                input[type="radio"]:checked::before {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 8px;
                    height: 8px;
                    background: #1072BA;
                    border-radius: 50%;
                }
            `}</style>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: isMobile ? '12px' : isTablet ? '40px' : '64px',
                    gap: '40px',
                    minHeight: '100vh',
                    width: '100%',
                    backgroundColor: '#1C1D1F',
                    boxSizing: 'border-box',
                    position: 'relative',
                    left: '0',
                    top: '0',
                    overflowX: 'hidden'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: (isMobile || isTablet) ? 'column' : 'row',
                        alignItems: (isMobile || isTablet) ? 'center' : 'flex-start',
                        padding: '0px',
                        gap: isMobile ? '40px' : isTablet ? '56px' : '72px',
                        width: '100%',
                        maxWidth: '1260px'
                    }}
                >
                    {/* Left Column - Content */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: (isMobile || isTablet) ? 'center' : 'flex-start',
                            justifyContent: 'space-between',
                            padding: '0px',
                            width: (isMobile || isTablet) ? '100%' : '573px',
                            height: 'auto',
                            minHeight: '600px',
                            flex: 'none',
                            order: 0,
                            flexGrow: 1,
                            textAlign: (isMobile || isTablet) ? 'center' : 'left'
                        }}
                    >
                        {/* Header section */}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: (isMobile || isTablet) ? 'center' : 'flex-start',
                                padding: '0px',
                                gap: isMobile ? '24px' : isTablet ? '32px' : '40px',
                                width: '100%',
                                maxWidth: '573px',
                                flex: 'none',
                                order: 0,
                                alignSelf: 'stretch',
                                flexGrow: 0
                            }}
                        >
                            <h1
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    fontFamily: 'Archivo',
                                    fontStyle: 'normal',
                                    fontWeight: 700,
                                    fontSize: isMobile ? '28px' : isTablet ? '36px' : '44px',
                                    lineHeight: '130%',
                                    color: '#EAEEF4',
                                    flex: 'none',
                                    order: 0,
                                    alignSelf: 'stretch',
                                    flexGrow: 0,
                                    margin: 0,
                                    textAlign: (isMobile || isTablet) ? 'center' : 'left'
                                }}
                            >
                                {currentPhase ? `${currentPhase?.title || ''}` : ('')}
                            </h1>
                            <p
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: '#A7A9BE',
                                    flex: 'none',
                                    order: 1,
                                    alignSelf: 'stretch',
                                    flexGrow: 0,
                                    margin: 0,
                                    textAlign: (isMobile || isTablet) ? 'center' : 'left'
                                }}
                            >
                                {currentPhase?.description || currentSection?.description || ''}
                            </p>
                        </div>

                        {/* Middle section - Phase buttons */}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: (isMobile || isTablet) ? 'center' : 'flex-start',
                                padding: '0px',
                                gap: '12px',
                                width: isMobile ? '150px' : isTablet ? '160px' : '172px',
                                maxHeight: isMobile ? '200px' : '300px',
                                overflowY: 'auto',
                                flex: 'none',
                                order: 1,
                                flexGrow: 0
                            }}
                        >
                            {currentSectionPhases.map((phase: any, index: number) => {
                                const isCompleted = index < phaseIndex;
                                const isCurrent = index === phaseIndex;
                                const isAccessible = isCompleted || isCurrent;

                                return (
                                    <div
                                        key={`${sectionIndex}-${index}`}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            padding: '6px 12px',
                                            gap: '8px',
                                            width: isMobile ? '150px' : isTablet ? '160px' : '172px',
                                            minHeight: isMobile ? '30px' : '32px',
                                            background: isCurrent ? '#36363A' : isCompleted ? '#2A2A2D' : 'transparent',
                                            border: isAccessible ? 'none' : '1px solid #7A7B8A',
                                            borderRadius: '20px',
                                            flex: 'none',
                                            order: index,
                                            flexGrow: 0,
                                            cursor: isAccessible ? 'pointer' : 'default',
                                            opacity: isCurrent ? 1 : isCompleted ? 0.8 : 0.5
                                        }}
                                        onClick={() => {
                                            if (isAccessible && !isCurrent) {
                                                updatePosition(sectionIndex, index);
                                                setFormData(initialFormData);
                                                setValues({});
                                            }
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontFamily: 'Inter',
                                                fontStyle: 'normal',
                                                fontWeight: 400,
                                                fontSize: isMobile ? '13px' : isTablet ? '14px' : '14px',
                                                lineHeight: '140%',
                                                textAlign: 'center',
                                                fontFeatureSettings: "'calt' off",
                                                color: isAccessible ? '#FFFFFF' : '#A7A9BE',
                                                flex: 'none',
                                                order: 0,
                                                flexGrow: 0
                                            }}
                                        >
                                            {phase?.name || `Phase ${index + 1}`}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Bottom section - Social media icons */}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: (isMobile || isTablet) ? 'center' : 'flex-start',
                                alignItems: 'center',
                                padding: '0px',
                                gap: '15px',
                                width: '141px',
                                height: '24px',
                                flex: 'none',
                                order: 2,
                                flexGrow: 0
                            }}
                        >
                            {/* Facebook */}
                            <a href="https://www.facebook.com/SmartCat.io" target="_blank" rel="noopener noreferrer" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="10" height="19" viewBox="0 0 10 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9.5 3.5H6.5C5.9477 3.5 5.5 3.94772 5.5 4.5V7.5H9.5C9.6137 7.49748 9.7216 7.5504 9.7892 7.6419C9.8568 7.7334 9.8758 7.852 9.84 7.96L9.1 10.16C9.0318 10.3619 8.8431 10.4984 8.63 10.5H5.5V18C5.5 18.2761 5.2761 18.5 5 18.5H2.5C2.22386 18.5 2 18.2761 2 18V10.5H0.5C0.22386 10.5 0 10.2761 0 10V8C0 7.7239 0.22386 7.5 0.5 7.5H2V4.5C2 2.29086 3.7909 0.5 6 0.5H9.5C9.7761 0.5 10 0.72386 10 1V3C10 3.27614 9.7761 3.5 9.5 3.5Z" fill="#A7A9BE"/>
                                </svg>
                            </a>

                            {/* Instagram */}
                            <a href="https://www.instagram.com/smartcat.io/" target="_blank" rel="noopener noreferrer" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M13 0.5H5C2.23858 0.5 0 2.73858 0 5.5V13.5C0 16.2614 2.23858 18.5 5 18.5H13C15.7614 18.5 18 16.2614 18 13.5V5.5C18 2.73858 15.7614 0.5 13 0.5ZM16.25 13.5C16.2445 15.2926 14.7926 16.7445 13 16.75H5C3.20735 16.7445 1.75549 15.2926 1.75 13.5V5.5C1.75549 3.70735 3.20735 2.25549 5 2.25H13C14.7926 2.25549 16.2445 3.70735 16.25 5.5V13.5ZM13.75 5.75C14.3023 5.75 14.75 5.30228 14.75 4.75C14.75 4.19772 14.3023 3.75 13.75 3.75C13.1977 3.75 12.75 4.19772 12.75 4.75C12.75 5.30228 13.1977 5.75 13.75 5.75ZM9 5C6.51472 5 4.5 7.01472 4.5 9.5C4.5 11.9853 6.51472 14 9 14C11.4853 14 13.5 11.9853 13.5 9.5C13.5027 8.3057 13.0294 7.15957 12.1849 6.31508C11.3404 5.47059 10.1943 4.99734 9 5ZM6.25 9.5C6.25 11.0188 7.4812 12.25 9 12.25C10.5188 12.25 11.75 11.0188 11.75 9.5C11.75 7.9812 10.5188 6.75 9 6.75C7.4812 6.75 6.25 7.9812 6.25 9.5Z" fill="#A7A9BE"/>
                                </svg>
                            </a>

                            {/* The X */}
                            <a href="https://x.com/SmartCat_io" target="_blank" rel="noopener noreferrer" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.1328 17.3478L11.264 8.12469L17.0549 1.75438C17.1859 1.60674 17.2534 1.41343 17.2428 1.21636C17.2322 1.01929 17.1444 0.834346 16.9983 0.701615C16.8523 0.568884 16.6598 0.49907 16.4626 0.507309C16.2654 0.515547 16.0794 0.601174 15.9449 0.745625L10.4287 6.81312L6.63276 0.847813C6.56507 0.74127 6.47158 0.653535 6.36097 0.592735C6.25035 0.531936 6.12617 0.500039 5.99994 0.5H1.49994C1.36547 0.499935 1.23345 0.536026 1.11771 0.604494C1.00198 0.672962 0.906774 0.771288 0.842079 0.889177C0.777384 1.00707 0.745575 1.14018 0.749983 1.27458C0.75439 1.40898 0.794852 1.53973 0.867132 1.65313L6.73588 10.8753L0.944945 17.2503C0.87732 17.323 0.824785 17.4083 0.790386 17.5014C0.755987 17.5945 0.740407 17.6935 0.74455 17.7927C0.748693 17.8918 0.772477 17.9892 0.814522 18.0791C0.856566 18.169 0.916036 18.2496 0.989485 18.3164C1.06293 18.3831 1.1489 18.4346 1.2424 18.4679C1.3359 18.5012 1.43508 18.5156 1.53419 18.5103C1.63329 18.5049 1.73035 18.48 1.81974 18.4369C1.90914 18.3937 1.98908 18.3333 2.05494 18.2591L7.5712 12.1916L11.3671 18.1569C11.4354 18.2625 11.5291 18.3494 11.6397 18.4093C11.7503 18.4693 11.8742 18.5005 11.9999 18.5H16.4999C16.6343 18.5 16.7661 18.4638 16.8817 18.3954C16.9973 18.327 17.0924 18.2288 17.1571 18.111C17.2218 17.9933 17.2536 17.8604 17.2493 17.7261C17.2451 17.5918 17.2048 17.4612 17.1328 17.3478ZM12.4115 17L2.86588 2H5.58463L15.134 17H12.4115Z" fill="#A7A9BE"/>
                                </svg>
                            </a>

                            {/* LinkedIn */}
                            <a href="https://www.linkedin.com/company/smartcat-io/" target="_blank" rel="noopener noreferrer" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M2 0.5H16C17.1046 0.5 18 1.39543 18 2.5V16.5C18 17.6046 17.1046 18.5 16 18.5H2C0.89543 18.5 0 17.6046 0 16.5V2.5C0 1.39543 0.89543 0.5 2 0.5ZM5 15.5C5.27614 15.5 5.5 15.2761 5.5 15V8C5.5 7.7239 5.27614 7.5 5 7.5H3.5C3.22386 7.5 3 7.7239 3 8V15C3 15.2761 3.22386 15.5 3.5 15.5H5ZM4.25 6.5C3.42157 6.5 2.75 5.82843 2.75 5C2.75 4.17157 3.42157 3.5 4.25 3.5C5.07843 3.5 5.75 4.17157 5.75 5C5.75 5.82843 5.07843 6.5 4.25 6.5ZM14.5 15.5C14.7761 15.5 15 15.2761 15 15V10.4C15.0325 8.8108 13.8576 7.45452 12.28 7.26C11.177 7.15925 10.1083 7.6744 9.5 8.6V8C9.5 7.7239 9.2761 7.5 9 7.5H7.5C7.2239 7.5 7 7.7239 7 8V15C7 15.2761 7.2239 15.5 7.5 15.5H9C9.2761 15.5 9.5 15.2761 9.5 15V11.25C9.5 10.4216 10.1716 9.75 11 9.75C11.8284 9.75 12.5 10.4216 12.5 11.25V15C12.5 15.2761 12.7239 15.5 13 15.5H14.5Z" fill="#A7A9BE"/>
                                </svg>
                            </a>
                        </div>
                    </div>

                    {/* Right Column - Form */}
                    <form onSubmit={nextClick}
                        noValidate
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            padding: isMobile ? '16px' : isTablet ? '32px' : '40px',
                            margin: '0 0 40px 0',
                            width: '100%',
                            maxWidth: (isMobile || isTablet) ? 'none' : '615px',
                            height: 'auto',
                            background: '#27272A',
                            borderRadius: '16px',
                            flex: 'none',
                            order: 1,
                            flexGrow: 0,
                            boxSizing: 'border-box',
                            justifyContent: 'space-between'
                        }}
                    >
                        {/* Questions Container */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: isMobile ? '20px' : isTablet ? '24px' : '30px',
                            width: '100%',
                            flex: '1 1 auto'
                        }}>
                            {/* Questions */}
                            {getCurrentPhaseQuestions().map((question: any, qIndex: number) => (
                            <div key={question.id} style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                padding: '0px',
                                gap: isMobile ? '20px' : isTablet ? '24px' : '30px',
                                width: '100%',
                                height: 'auto',
                                flex: 'none',
                                order: qIndex,
                                alignSelf: 'stretch',
                                flexGrow: 0
                            }}>
                                <div style={{
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'flex-start',
                                    padding: '0px',
                                    gap: isMobile ? '8px' : '10px',
                                    width: '100%',
                                    height: 'auto',
                                    border: '1px solid #27272A',
                                    borderRadius: '8px',
                                    flex: 'none',
                                    order: 0,
                                    alignSelf: 'stretch',
                                    flexGrow: 0
                                }}>
                                    {/* Question text */}
                                    <h3 style={{
                                        fontFamily: 'Inter',
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
                                        lineHeight: '150%',
                                        fontFeatureSettings: "'calt' off",
                                        color: '#FFFFFF',
                                        margin: 0,
                                        padding: '0px 8px',
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word',
                                        hyphens: 'auto',
                                        width: 'calc(100% - 16px)',
                                        boxSizing: 'border-box',
                                        whiteSpace: 'normal'
                                    }}>
                                        {(question?.text || '').replace("{companyName}", filledQuestionnaire.companyName)} <span style={{ color: '#45C5E0' }}>*</span>
                                    </h3>

                                    {/* Answer options */}
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        padding: '0px 8px',
                                        gap: '8px',
                                        width: '100%',
                                        height: 'auto',
                                        flex: 'none',
                                        order: 1,
                                        alignSelf: 'stretch',
                                        flexGrow: 0
                                    }}>
                                        {question?.answers.map((answer: any) => (
                                            <label key={answer.id} style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'flex-start',
                                                padding: '0px',
                                                gap: '10px',
                                                minHeight: '24px',
                                                width: '100%',
                                                cursor: 'pointer'
                                            }}>
                                                <input
                                                    type="radio"
                                                    name={question.id}
                                                    value={answer.id}
                                                    checked={values[question.id] === answer.id}
                                                    onChange={() => fieldChanged(answer, question.id)}
                                                    required={question.isMandatory}
                                                    style={{
                                                        boxSizing: 'border-box',
                                                        width: '16px',
                                                        height: '16px',
                                                        minWidth: '16px',
                                                        border: '1px solid #7A7B8A',
                                                        borderRadius: '50%',
                                                        flexShrink: 0,
                                                        appearance: 'none',
                                                        backgroundColor: 'transparent',
                                                        cursor: 'pointer',
                                                        position: 'relative',
                                                        marginTop: '4px'
                                                    }}
                                                />
                                                <span style={{
                                                    fontFamily: 'Inter',
                                                    fontStyle: 'normal',
                                                    fontWeight: 400,
                                                    fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
                                                    lineHeight: '150%',
                                                    fontFeatureSettings: "'calt' off",
                                                    color: '#FFFFFF',
                                                    display: 'inline-block',
                                                    width: 'calc(100% - 26px)'
                                                }}>
                                                    {answer.text}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div style={{
                                    width: '100%',
                                    height: '0px',
                                    border: '1px solid #36363A',
                                    flex: 'none',
                                    order: 0,
                                    alignSelf: 'stretch',
                                    flexGrow: 0
                                }} />
                            </div>
                            ))}
                        </div>

                        {/* Buttons */}
                        <div style={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            justifyContent: isMobile ? 'center' : 'flex-end',
                            alignItems: 'center',
                            padding: isMobile ? '20px 0px 0px' : isTablet ? '25px 0px 0px' : '30px 0px 0px',
                            gap: isMobile ? '12px' : isTablet ? '18px' : '22px',
                            width: '100%',
                            height: 'auto',
                            flex: '0 0 auto',
                            alignSelf: 'stretch'
                        }}>
                            {/* Previous button - hidden on first phase of any section */}
                            {!(phaseIndex === 0) && (
                                <button
                                    type="button"
                                    onClick={previousClick}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: isMobile ? '12px 24px' : isTablet ? '14px 40px' : '16px 51px',
                                        gap: '2px',
                                        width: isMobile ? '100%' : isTablet ? '150px' : '168px',
                                        height: isMobile ? '48px' : isTablet ? '52px' : '56px',
                                        background: '#36363A',
                                        backdropFilter: 'blur(4px)',
                                        borderRadius: '8px',
                                        flex: 'none',
                                        order: 0,
                                        flexGrow: 0,
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontFamily: 'Inter',
                                        fontStyle: 'normal',
                                        fontWeight: 500,
                                        fontSize: isMobile ? '14px' : isTablet ? '15px' : '16px',
                                        lineHeight: '150%',
                                        fontFeatureSettings: "'calt' off",
                                        color: '#FFFFFF'
                                    }}
                                >
                                    Previous
                                </button>
                            )}

                            {/* Continue button */}
                            <button
                                type="submit"
                                style={{
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: isMobile ? '12px 24px' : '16px 51px',
                                    gap: '2px',
                                    width: isMobile ? '100%' : isTablet ? '150px' : '172px',
                                    height: '56px',
                                    background: 'linear-gradient(91.9deg, #145F96 0.8%, #249FDA 99.45%)',
                                    borderRadius: '8px',
                                    flex: 'none',
                                    order: 1,
                                    flexGrow: 0,
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    lineHeight: '150%',
                                    fontFeatureSettings: "'calt' off",
                                    color: '#FFFFFF'
                                }}
                            >
                                Continue
                            </button>
                        </div>
                    </form>
                </div>

                {/* Toast notification */}
                {showToast && (
                    <div
                        style={{
                            boxSizing: 'border-box',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            padding: isMobile ? '16px' : isTablet ? '18px' : '20px',
                            gap: isMobile ? '12px' : isTablet ? '14px' : '16px',
                            position: 'fixed',
                            bottom: isMobile ? '20px' : isTablet ? '30px' : '40px',
                            right: isMobile ? '20px' : isTablet ? '30px' : '40px',
                            width: isMobile ? 'calc(100% - 40px)' : '404px',
                            maxWidth: '404px',
                            minHeight: '102px',
                            height: 'auto',
                            background: 'linear-gradient(93.93deg, #2D82B2 -6.52%, #329ABB 108.61%)',
                            border: '1px solid #7BCFED',
                            borderRadius: '12px',
                            zIndex: 1000,
                            animation: 'slideIn 0.3s ease-out'
                        }}
                    >
                        {/* Icon */}
                        <div
                            style={{
                                width: '24px',
                                height: '24px',
                                flex: 'none',
                                order: 0,
                                flexGrow: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect opacity="0.3" width="24" height="24" rx="6" fill="black"/>
                                <path d="M12 16V11" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                                <circle cx="1" cy="1" r="1" transform="matrix(1 0 0 -1 11 9)" fill="white"/>
                            </svg>
                        </div>

                        {/* Content */}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                padding: '0px',
                                gap: '4px',
                                width: '324px',
                                height: '62px',
                                flex: 'none',
                                order: 1,
                                flexGrow: 0
                            }}
                        >
                            <h4
                                style={{
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    lineHeight: '150%',
                                    color: '#FFFFFF',
                                    margin: 0,
                                    flex: 'none',
                                    order: 0,
                                    alignSelf: 'stretch',
                                    flexGrow: 0
                                }}
                            >
                                Incomplete Form
                            </h4>
                            <p
                                style={{
                                    fontFamily: 'Inter',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: '150%',
                                    color: '#E8F4F8',
                                    margin: 0,
                                    flex: 'none',
                                    order: 1,
                                    alignSelf: 'stretch',
                                    flexGrow: 0
                                }}
                            >
                                Please complete all required fields before proceeding to the next page.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes slideIn {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
            `}</style>
        </>
    )
}

export default Questionnaire;